import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { announce } from '../ui/LiveAnnouncer';
import { apiErrorMessage } from '../sales/shared';
import type { ActiveTimer } from '../../types';
import { statusAfterStop } from './timerModel';
import { showStatusAfterStop } from './timerFlow';
import { toastUnderMinute } from './underMinuteToast';

/**
 * P-10a (D-64) — ⏸ "עצירה": the ONE stop of a timer, everywhere (the floating
 * timers, Home, the ticket page, the work-logs page). It stops at once, with
 * NO window: the segment is saved, the log (work record) stays open and ▶
 * continues it. A task's status circles then show for a moment
 * (`showStatusAfterStop`); changing the status is optional.
 */
export function useStopTimer(opts: { onStopped?: (timer: ActiveTimer) => void } = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (timer: ActiveTimer) => api.stopTimer(timer.id),
    onSuccess: (res, timer) => {
      // FQ-062 (Q-176) — under a minute: nothing was recorded.
      if (res?.data?.skipped_short) toastUnderMinute(t);
      const after = statusAfterStop(timer);
      if (after) showStatusAfterStop(after);
      else announce(t('timer.pausedTitle', { title: timer.ticket_title ?? '' }));
      opts.onStopped?.(timer);
    },
    onError: (err) => toast('error', apiErrorMessage(err, t('timer.stopError'))),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['timer-status'] });
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      queryClient.invalidateQueries({ queryKey: ['work-records'] });
    },
  });
}
